import { useEffect, useState } from "react";
import { api, errText, photoUrl } from "../../lib/api";
import { Avatar, AvatarFallback, AvatarImage } from "../ui/avatar";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "../ui/dialog";
import { toast } from "sonner";
import { MessageCircle, ArrowLeft } from "lucide-react";

export function MemberConversationsDialog({ user, onClose }) {
  const [convs, setConvs] = useState(null);
  const [active, setActive] = useState(null);
  const [thread, setThread] = useState(null);
  useEffect(() => {
    api.get(`/admin/users/${user.user_id}/conversations`).then((r) => setConvs(r.data.conversations)).catch((e) => toast.error(errText(e)));
  }, [user]);
  const openThread = (c) => {
    setActive(c); setThread(null);
    api.get(`/staff/conversations/${c.conversation_id}/messages`).then((r) => setThread(r.data.messages)).catch((e) => toast.error(errText(e)));
  };
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="sm:max-w-2xl bg-white max-h-[85vh] overflow-hidden flex flex-col" data-testid="member-conversations-dialog">
        <DialogHeader>
          <DialogTitle className="font-display text-[#12234A] flex items-center gap-2">
            {active ? <button onClick={() => { setActive(null); setThread(null); }} data-testid="member-conv-back" className="text-slate-400 hover:text-[#12234A]"><ArrowLeft size={18} /></button> : <MessageCircle size={18} className="text-[#F5551E]" />}
            {active ? `Chat with ${active.other_name}` : `${user.name}'s conversations`}
          </DialogTitle>
          <DialogDescription className="text-slate-500">{active ? (active.listing_title ? `Re: ${active.listing_title}` : "Direct conversation") : "Member-to-member messages (read-only)."}</DialogDescription>
        </DialogHeader>
        <div className="overflow-y-auto flex-1">
          {!active ? (
            convs === null ? <div className="py-10 text-center text-sm text-slate-400">Loading…</div>
            : convs.length === 0 ? <div className="py-10 text-center text-sm text-slate-400" data-testid="member-conv-empty">No member conversations.</div>
            : <div className="space-y-1.5">
                {convs.map((c) => (
                  <button key={c.conversation_id} onClick={() => openThread(c)} data-testid={`member-conv-row-${c.conversation_id}`} className="w-full flex items-center gap-3 p-2.5 rounded-xl border border-slate-100 hover:bg-slate-50 text-left">
                    <Avatar className="h-9 w-9 shrink-0"><AvatarImage src={photoUrl(c.other_picture)} /><AvatarFallback className="bg-[#12234A] text-white text-xs">{c.other_name?.[0]}</AvatarFallback></Avatar>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-semibold text-[#12234A] truncate">{c.other_name}{c.listing_title ? <span className="text-xs font-normal text-slate-400"> · {c.listing_title}</span> : null}</p>
                      <p className="text-xs text-slate-400 truncate">{c.last_message}</p>
                    </div>
                    {c.unread > 0 && <span className="shrink-0 min-w-[18px] h-[18px] px-1 rounded-full bg-[#F5551E] text-white text-[10px] font-bold flex items-center justify-center">{c.unread}</span>}
                  </button>
                ))}
              </div>
          ) : (
            thread === null ? <div className="py-10 text-center text-sm text-slate-400">Loading…</div>
            : <div className="space-y-2 py-1">
                {thread.map((m) => (
                  <div key={m.id} data-testid={`member-msg-${m.id}`} className={`max-w-[80%] rounded-2xl px-3.5 py-2 text-sm ${m.sender_id === user.user_id ? "ml-auto bg-[#12234A] text-white" : "bg-slate-100 text-slate-700"}`}>
                    <p className="text-[10px] font-semibold opacity-60 mb-0.5">{m.sender_name}{!m.read && m.sender_id !== user.user_id ? " · unread" : ""}</p>
                    {m.body || <span className="italic opacity-60">[deleted]</span>}
                  </div>
                ))}
                {thread.length === 0 && <div className="py-8 text-center text-sm text-slate-400">No messages.</div>}
              </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
